/**
 * matte.js — per-frame silhouettes, so the performer can walk across the seam.
 *
 * A straight seam is enough for most clone shots and costs nothing. It breaks
 * the moment the performer crosses it: half a shoulder disappears. The fix is a
 * mask shaped like the person, and this file offers two ways to get one.
 *
 * ── 1. CLEAN PLATE (default) ─────────────────────────────────────────────
 * This is the method a clone shoot makes possible and a generic video editor
 * cannot use: the camera did not move, so a per-pixel temporal MEDIAN over the
 * take is the empty room. Anything that differs from the empty room is the
 * performer. No model, no download, no GPU — a few dozen decoded frames and
 * some arithmetic.
 *
 * It has one honest failure mode, and the tool reports it rather than shipping
 * a broken matte: if the performer barely moves, they ARE the median, and the
 * silhouette comes out empty. `quality()` measures that and says so.
 *
 * ── 2. AI SILHOUETTE (fallback) ───────────────────────────────────────────
 * RobustVideoMatting via window.VbrRvmEngine — the same engine /video-bg-remover
 * ships. Works when the performer never moves, costs a ~14 MB model download and
 * runs per frame, so it is the second choice, not the first.
 *
 * Both produce the same thing: a canvas whose ALPHA is the matte, sized to the
 * clip's own frame, which scene.js draws over the layer with destination-in.
 *
 * Exposes window.CloneMatte.
 */
(function () {
'use strict';
if (window.CloneMatte) return;

var PLATE_W = 320;             // plate/matte working width — feather hides the rest
var PLATE_SAMPLES = 18;

function workSize(vw, vh) {
    var w = PLATE_W;
    var h = Math.max(8, Math.round(w * (vh / Math.max(1, vw))));
    return { w: w, h: h };
}

/* ─────────────────────── clean plate construction ─────────────────────── */

function seek(v, t) {
    return new Promise(function (resolve) {
        var done = false;
        var fin = function () { if (done) return; done = true; v.onseeked = null; resolve(); };
        v.onseeked = fin;
        setTimeout(fin, 3000);
        try { v.currentTime = t; } catch (_) { fin(); }
    });
}

/**
 * @param clip  a CloneScene clip
 * @returns { plate: ImageData, w, h, coverage }  or null
 */
async function buildPlate(clip, onProgress, signal, samples) {
    var v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.preload = 'auto';
    v.src = clip.url;
    await new Promise(function (res) {
        var ok = false;
        var fin = function () { if (!ok) { ok = true; res(); } };
        v.onloadeddata = fin; v.onerror = fin;
        setTimeout(fin, 15000);
    });
    if (!v.videoWidth) return null;

    var sz = workSize(v.videoWidth, v.videoHeight);
    var cv = document.createElement('canvas');
    cv.width = sz.w; cv.height = sz.h;
    var ctx = cv.getContext('2d', { willReadFrequently: true });

    var t0 = clip.trimIn || 0;
    var t1 = t0 + Math.max(0.1, window.CloneScene.clipDuration(clip));
    var n = Math.max(3, samples || PLATE_SAMPLES);
    var stacks = [];

    for (var i = 0; i < n; i++) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        // Sample across the WHOLE take, not the first seconds: the performer has
        // to have vacated each pixel at least half the time for the median to
        // land on the room, and they only do that over the full span.
        var t = t0 + (t1 - t0) * (i + 0.5) / n;
        await seek(v, Math.min(t, (v.duration || t1) - 0.05));
        ctx.drawImage(v, 0, 0, sz.w, sz.h);
        stacks.push(ctx.getImageData(0, 0, sz.w, sz.h).data);
        onProgress && onProgress((i + 1) / n);
    }
    try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}

    var px = sz.w * sz.h;
    var plate = new Uint8ClampedArray(px * 4);
    var mid = stacks[n >> 1];
    var scratch = new Uint8Array(n);
    for (var p = 0; p < px; p++) {
        var o = p * 4;
        for (var ch = 0; ch < 3; ch++) {
            for (var s = 0; s < n; s++) scratch[s] = stacks[s][o + ch];
            plate[o + ch] = median(scratch, n);
        }
        plate[o + 3] = 255;
    }

    return { plate: plate, w: sz.w, h: sz.h, sample: mid, samples: n };
}

function median(arr, n) {
    // Insertion sort on a copy — n is 18, and a TypedArray.sort() allocation per
    // pixel would dominate the whole build.
    var a = Array.prototype.slice.call(arr, 0, n);
    a.sort(function (x, y) { return x - y; });
    return a[n >> 1];
}

/* ─────────────────────── per-frame matte from a plate ─────────────────────── */

function PlateMatte(plateInfo) {
    this.info = plateInfo;
    this.w = plateInfo.w;
    this.h = plateInfo.h;

    this.src = document.createElement('canvas');
    this.src.width = this.w; this.src.height = this.h;
    this.sctx = this.src.getContext('2d', { willReadFrequently: true });

    this.out = document.createElement('canvas');
    this.out.width = this.w; this.out.height = this.h;
    this.octx = this.out.getContext('2d');

    this.field = new Float32Array(this.w * this.h);
    this.tmp = new Float32Array(this.w * this.h);
    this.threshold = 22;
    this.grow = 1;
}

/**
 * @param frame  any CanvasImageSource showing the clip at this moment
 * @returns the matte canvas (alpha = subject). Reused between calls.
 */
PlateMatte.prototype.compute = function (frame) {
    var w = this.w, h = this.h;
    this.sctx.drawImage(frame, 0, 0, w, h);
    var cur = this.sctx.getImageData(0, 0, w, h).data;
    var plate = this.info.plate;
    var f = this.field;

    for (var p = 0, o = 0; p < f.length; p++, o += 4) {
        // Max channel deviation, not the mean: a red shirt against a red wall
        // differs in one channel only, and averaging it away loses the arm.
        var dr = Math.abs(cur[o] - plate[o]);
        var dg = Math.abs(cur[o + 1] - plate[o + 1]);
        var db = Math.abs(cur[o + 2] - plate[o + 2]);
        f[p] = Math.max(dr, Math.max(dg, db));
    }

    blur(f, this.tmp, w, h, 2);
    hysteresis(f, w, h, this.threshold, this.threshold * 2.2);
    for (var g = 0; g < this.grow; g++) dilate(f, this.tmp, w, h);
    blur(f, this.tmp, w, h, 2);

    var img = this.octx.createImageData(w, h);
    var d = img.data;
    for (var i = 0, j = 0; i < f.length; i++, j += 4) {
        d[j] = 255; d[j + 1] = 255; d[j + 2] = 255;
        d[j + 3] = Math.max(0, Math.min(255, Math.round(f[i] * 255)));
    }
    this.octx.putImageData(img, 0, 0);
    return this.out;
};

/** Share of the frame the matte claims — the honesty check. */
PlateMatte.prototype.coverage = function () {
    var f = this.field, n = 0;
    for (var i = 0; i < f.length; i++) if (f[i] > 0.5) n++;
    return n / f.length;
};

function blur(f, tmp, w, h, r) {
    // Separable box blur, two passes.
    var i, x, y, s, n, k;
    for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
            s = 0; n = 0;
            for (k = -r; k <= r; k++) {
                var xx = x + k; if (xx < 0 || xx >= w) continue;
                s += f[y * w + xx]; n++;
            }
            tmp[y * w + x] = s / n;
        }
    }
    for (x = 0; x < w; x++) {
        for (y = 0; y < h; y++) {
            s = 0; n = 0;
            for (k = -r; k <= r; k++) {
                var yy = y + k; if (yy < 0 || yy >= h) continue;
                s += tmp[yy * w + x]; n++;
            }
            f[y * w + x] = s / n;
        }
    }
}

/* Two thresholds: a pixel is subject if it is strongly different, OR mildly
   different and touching something that is. One threshold either eats the
   subject's soft edges or promotes every compression artefact in the room. */
function hysteresis(f, w, h, lo, hi) {
    var n = f.length;
    var mark = new Uint8Array(n);
    var stack = [];
    for (var i = 0; i < n; i++) {
        if (f[i] >= hi) { mark[i] = 1; stack.push(i); }
    }
    while (stack.length) {
        var idx = stack.pop();
        var x = idx % w, y = (idx / w) | 0;
        for (var dy = -1; dy <= 1; dy++) {
            for (var dx = -1; dx <= 1; dx++) {
                var xx = x + dx, yy = y + dy;
                if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
                var j = yy * w + xx;
                if (mark[j] || f[j] < lo) continue;
                mark[j] = 1; stack.push(j);
            }
        }
    }
    for (var k = 0; k < n; k++) f[k] = mark[k] ? 1 : 0;
}

function dilate(f, tmp, w, h) {
    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            var m = 0;
            for (var dy = -1; dy <= 1 && !m; dy++) {
                for (var dx = -1; dx <= 1; dx++) {
                    var xx = x + dx, yy = y + dy;
                    if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
                    if (f[yy * w + xx] > 0.5) { m = 1; break; }
                }
            }
            tmp[y * w + x] = m;
        }
    }
    f.set(tmp);
}

/* ─────────────────────────── AI silhouette ─────────────────────────── */

function AiMatte() {
    this.w = 0; this.h = 0;
    this.src = null; this.sctx = null;
    this.out = document.createElement('canvas');
    this.octx = this.out.getContext('2d');
}

AiMatte.available = function () { return !!window.VbrRvmEngine; };

AiMatte.prototype.ready = function (signal) {
    if (!window.VbrRvmEngine) return Promise.reject(new Error('no-rvm'));
    return window.VbrRvmEngine.ready(signal);
};

AiMatte.prototype.reset = function () {
    try { window.VbrRvmEngine.resetState(); } catch (_) {}
};

AiMatte.prototype.compute = async function (frame, vw, vh, sequential) {
    var sz = workSize(vw || 640, vh || 360);
    if (!this.src || this.w !== sz.w || this.h !== sz.h) {
        this.w = sz.w; this.h = sz.h;
        this.src = document.createElement('canvas');
        this.src.width = sz.w; this.src.height = sz.h;
        this.sctx = this.src.getContext('2d');
        this.out.width = sz.w; this.out.height = sz.h;
    }
    this.sctx.drawImage(frame, 0, 0, this.w, this.h);
    // matte() feeds RVM's recurrent state forward, which is what removes the
    // per-frame edge flicker — but it is only correct when frames arrive in
    // order. The preview scrubs around, so it asks for the stateless variant.
    var res = sequential
        ? await window.VbrRvmEngine.matte(this.src)
        : await window.VbrRvmEngine.matteStateless(this.src);
    this.octx.clearRect(0, 0, this.w, this.h);
    this.octx.drawImage(res, 0, 0, this.w, this.h);
    return this.out;
};

/**
 * Where the performer stands in this take — the answer that lets the tool open
 * with BOTH people already visible instead of an arbitrary half of the frame.
 *
 * Uses a cheap 4-sample plate: the subject is whatever differs from the empty
 * room, and its centroid says which part of the frame this take contributes.
 * Returns null when nothing stands out (a take where nobody moved), so the
 * caller falls back to a neutral seam rather than to a confident wrong guess.
 *
 * @returns { x, y, coverage } with x/y normalised 0..1, or null
 */
function subjectCentroid(plateInfo) {
    if (!plateInfo || !plateInfo.sample) return null;
    var w = plateInfo.w, h = plateInfo.h;
    var cur = plateInfo.sample, plate = plateInfo.plate;
    var f = new Float32Array(w * h);
    for (var p = 0, o = 0; p < f.length; p++, o += 4) {
        f[p] = Math.max(Math.abs(cur[o] - plate[o]),
               Math.max(Math.abs(cur[o + 1] - plate[o + 1]),
                        Math.abs(cur[o + 2] - plate[o + 2])));
    }
    var tmp = new Float32Array(w * h);
    blur(f, tmp, w, h, 2);

    var sx = 0, sy = 0, n = 0;
    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            if (f[y * w + x] < 26) continue;
            sx += x; sy += y; n++;
        }
    }
    if (n < f.length * 0.004) return null;
    return { x: sx / n / w, y: sy / n / h, coverage: n / f.length };
}

window.CloneMatte = {
    buildPlate: buildPlate,
    subjectCentroid: subjectCentroid,
    PlateMatte: PlateMatte,
    AiMatte: AiMatte,
    PLATE_SAMPLES: PLATE_SAMPLES
};
})();
